import { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { Card, CardContent, CardHeader, Switch } from '@roger/ui-components';
import { FlaskConical } from 'lucide-react';

import { setPatientTestStatusPatientsPatientIdTestStatusPatch as setPatientTestStatus } from '../../services/api/sdk.gen';
import type { PatientResponse } from '../../services/api/types.gen';
import { useStore } from '../../store';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';

interface PatientTestStatusToggleProps {
  patientId: string;
  isTest: boolean;
  /** Called with the server's updated patient after a confirmed change. */
  onUpdated: (patient: PatientResponse) => void;
}

/**
 * The "Test / Practice Patient" section of the Patient Info tab (ROG-1718).
 *
 * Deliberately lives here, low in a tab, rather than in the patient header
 * (Pedram/Keyur UX call): it stays out of the way of clinicians who don't use
 * it, and the explanatory copy sits right above the switch so a clinician reads
 * what marking does *before* flipping it. Available to every clinician — no
 * permission gate — because clinicians in training need to run test patients.
 *
 * Non-optimistic: the switch reflects server state only, flipping when the
 * parent applies the returned patient, so a failed save never leaves a patient
 * looking marked when they aren't. The flag does not block billing; the payer/
 * CMS exits warn-and-confirm or mock, so no consequences modal is needed here.
 */
export function PatientTestStatusToggle({
  patientId,
  isTest,
  onUpdated,
}: PatientTestStatusToggleProps) {
  const { showError } = useStore(
    useShallow(state => ({ showError: state.showError }))
  );
  const [saving, setSaving] = useState(false);

  const applyChange = async (next: boolean) => {
    setSaving(true);
    try {
      const { data } = await setPatientTestStatus({
        path: { patient_id: patientId },
        body: { is_test: next },
        throwOnError: true,
      });
      // Pessimistic: reflect server truth only after a confirmed save.
      if (data) onUpdated(data);
    } catch (err) {
      console.error('Failed to update patient test status', err);
      showError(getBusinessErrorMessage(err) ?? 'Failed to update test status');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader className='pb-4'>
        <div className='flex items-center gap-2'>
          <FlaskConical className='h-4 w-4 text-muted-foreground' />
          <h3 className='text-sm font-semibold'>Test / Practice Patient</h3>
        </div>
      </CardHeader>
      <CardContent className='space-y-4'>
        <p className='text-sm text-muted-foreground'>
          Use this for training or trying out a workflow. Roger will not run
          real benefits checks for a test patient, and will ask you to confirm
          before sending a real claim or Notice of Admission. Test patients are
          excluded from CMS survey files (OASIS and CAHPS). This only affects
          future actions — anything already submitted is not undone.
        </p>
        <div className='flex items-center gap-3'>
          <Switch
            id='patient-test-status-toggle'
            checked={isTest}
            disabled={saving}
            onCheckedChange={checked => void applyChange(checked)}
            aria-label='Mark as test patient'
          />
          <label
            htmlFor='patient-test-status-toggle'
            className='cursor-pointer text-sm'
          >
            {isTest ? 'This is a test patient' : 'Mark as a test patient'}
          </label>
        </div>
      </CardContent>
    </Card>
  );
}
